import { zodResolver } from "@hookform/resolvers/zod";
import { useIntersectionObserver, useWindowSize } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as z from "zod";
import { LeafButton } from "../components/buttons";
import { ContactItems } from "../components/contact/ContactItem";
import { https } from "../services/AxiosConfig";

export const formSchema = z.object({
  first_name: z.string().min(1, {
    message: "msg_error_1",
  }),
  last_name: z.string().min(1, {
    message: "msg_error_1",
  }),
  phone: z
    .string()
    .regex(/^(?:\+?84|0[35789])([0-9]{8,9})$/, "msg_error_4")
    .or(z.literal("")),
  email: z.intersection(
    z.string().min(1, {
      message: "msg_error_1",
    }),
    z.email("msg_error_2"),
  ),
  company_name: z.string(),
  product_name: z.string(),
  message: z.string().min(1, {
    message: "msg_error_1",
  }),
});

export default function ContactPage() {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const [focused, setFocused] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      company_name: "",
      product_name: "",
      message: "",
    },
  });

  const [ref3, entry3] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: "0px",
  });
  const [isVisible3, setIsVisible3] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function onSubmit(values) {
    setIsLoading(true);
    https
      .post("contact", values)
      .then(() => {
        reset();
        toast(t("content_106"), {
          className: "!bg-green-100 !border-green-500 !text-inherit",
        });
      })
      .catch(() => {
        toast(t("msg_error_3"), {
          className: "!bg-red-100 !border-red-500 !text-inherit",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if (entry3 && entry3.isIntersecting && !isVisible3) {
      setIsVisible3(true); // Đánh dấu đã gặp isIntersecting
    }
  }, [entry3, isVisible3]);

  return (
    <div>
      <Helmet>
        <title>{t("header.contact")} | Trusted Pay</title>
      </Helmet>
      <div className="mt-[80px]">
        <div className="flex flex-col items-center gap-[10px] bg-[#EFF0FF] px-4 py-[50px]">
          <h1 className="text-center sm600:text-5xl text-[64px] font-[700] capitalize not-italic leading-[70px] tracking-[-0.5px] text-[#565ADD]">
            {t("contact.title")}
          </h1>
          <div className="flex flex-col items-start self-stretch pb-5">
            <div className="mx-auto my-0 flex w-[16%] items-start self-stretch py-0">
              <span className="w-full self-stretch border-t-[5px] border-solid border-[#565ADD]"></span>
            </div>
          </div>
          <div className="flex flex-col items-center self-stretch">
            <p className="py-0 text-center font-semibold text-[#2B1C50]">
              {t("contact.des1")} {width > 768 ? <br /> : null}
              {t("contact.des2")}
            </p>
          </div>
        </div>
      </div>
      {/* section2 */}
      <div ref={ref3}>
        <div className={` ${isVisible3 ? "animate-zoomIn" : null}`}>
          <div className="mx-auto flex max-w-[1280px] flex-col py-[50px] pt-[50px] px-4">
            <div className="flex flex-col items-center justify-center ">
              <h1 className="max-w-[870px] text-center text-[36px] font-bold text-[--color-title]">
                {t("contact.contactUs.title1")}
                <span className="text-[--color-hover]">
                  {t("contact.contactUs.title2")}
                </span>
              </h1>
              <p className="text-4 max-w-[870px] text-center text-[16px] font-semibold text-[#3d2e7c]">
                {t("contact.contactUs.des")}
              </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mx-auto mt-5 grid w-[90%] md:grid-cols-2 gap-[12px] bg-[no-repeat] bg-contactSale bg-center">
                <ContactItems
                  label={t("homepage.first")}
                  placeholder={t("homepage.first1")}
                  border={true}
                  {...register("first_name")}
                  error={errors.first_name}
                />
                <ContactItems
                  label={t("homepage.lastName")}
                  placeholder={t("homepage.lastName1")}
                  border={true}
                  {...register("last_name")}
                  error={errors.last_name}
                />
                <ContactItems
                  label={t("homepage.yourphone")}
                  placeholder={t("homepage.yourphone1")}
                  border={true}
                  {...register("phone")}
                  error={errors.phone}
                />
                <ContactItems
                  label={t("homepage.email")}
                  placeholder={t("homepage.email1")}
                  border={true}
                  {...register("email")}
                  error={errors.email}
                />
                <ContactItems
                  label={t("homepage.company")}
                  placeholder={t("homepage.company1")}
                  border={true}
                  {...register("company_name")}
                  error={errors.company_name}
                />
                <ContactItems
                  label={t("homepage.products")}
                  placeholder={t("homepage.products1")}
                  border={true}
                  {...register("product_name")}
                  error={errors.product_name}
                />
                <div className="col-span-full flex flex-col px-[20px] py-[5px]">
                  <p className="text-4 mb-[4px] text-[--color-title]">
                    {t("homepage.mess")}
                  </p>
                  <textarea
                    className="h-[105px] min-h-[125px] w-full rounded-[30px] border border-[#565ADD] p-[12px] focus:border  focus:border-solid focus:border-[#565ADD] focus:shadow-focus-input focus:outline-none"
                    placeholder={focused ? "" : t("homepage.mess1")}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    type="text"
                    {...register("message")}
                  />
                  {errors.message && (
                    <span className="mt-1 text-sm text-red-500">
                      {t(errors.message.message)}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-6 flex items-center justify-center">
                <LeafButton
                  type="submit"
                  title={t("homepage.submit")}
                  disabled={isLoading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* section3 */}
      <div className="w-full bg-[#EFF0FF] px-4 md:px-[50px] pt-[50px]">
        <div className="mx-auto max-w-[1280px]">
          <h2 className="text-center text-[36px] font-bold text-[#2B1C50]">
            {t("contact.infor.title1")}{" "}
            <span className="text-[#565ADD]">{t("contact.infor.title2")}</span>
          </h2>
          <p className="mx-auto max-w-[1220px] py-[20px] text-center text-[16px] font-normal text-[#3d2e7c] space-x-1">
            <span>{t("contact.infor.des")}</span>
            <span className="cursor-pointer text-[#565ADD] hover:underline">
              {t("contact.infor.des2")}
            </span>
            <span>{t("contact.infor.des3")}</span>
          </p>
        </div>
      </div>
      {/* section4 */}
      <div className="w-full bg-[#565ADD] pb-[50px]">
        <div className="mx-auto grid max-w-[1280px] grid-cols-3 gap-[10px] rounded-[0px_0px_50px_50px] bg-[#EFF0FF] px-4 md:px-[50px] pb-[50px] sm:grid-cols-1">
          <div className="text-center lgmaxmin:mt-[30px] sm600:mt-[30px]">
            <h3 className="text-[24px] font-bold text-[#2B1C50]">
              {t("contact.infor.item1.title")}
            </h3>
            <p className="pt-[20px] text-[16px] font-normal leading-[22px] text-[#3d2e7c]">
              {t("contact.infor.item1.des1")}
            </p>
            <p className="pt-[5px] text-[16px] font-normal text-[#3d2e7c]">
              {t("contact.infor.item1.des2")}
            </p>
          </div>
          <div className="text-center lgmaxmin:mt-[30px] sm600:mt-[30px]">
            <h3 className="text-[24px] font-bold text-[#2B1C50]">
              {t("contact.infor.item2.title")}
            </h3>
            <a
              href={`tel:${t("contact.infor.item2.des1")}`}
              className="block pt-[20px] text-[16px] font-normal leading-[22px] text-[#3d2e7c]"
            >
              {t("contact.infor.item2.des1")}
            </a>
            <a
              href={`tel:${t("contact.infor.item2.des2")}`}
              className="block pt-[5px] text-[16px] font-normal text-[#3d2e7c]"
            >
              {t("contact.infor.item2.des2")}
            </a>
          </div>
          <div className="text-center lgmaxmin:mt-[30px] sm600:mt-[30px]">
            <h3 className="text-[24px] font-bold text-[#2B1C50]">
              {t("contact.infor.item3.title")}
            </h3>
            <a
              href="mailto:info@mobile-id.vn"
              className="pt-[20px] text-[16px] block font-normal leading-[22px] text-[#3d2e7c]"
            >
              {t("contact.infor.item3.des1")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
